import configJson from "./config.json";
import configNclJson from "./config-ncl.json";

import { compileTemplate, handleGraphqlQueryFilter, handleRESTQueryFilter, resolveResponsePath, isOfflineNCLMode, handleRESTQueryPayload } from "./utils";
import { parse } from "graphql";
import _ from 'lodash';
import { Response } from 'miragejs'

const config = isOfflineNCLMode() ? configNclJson : configJson;
const { seeders, origins, graphqlSeeders, passthroughURLs, timing } = compileTemplate(config);
const stubResponses = {}
const stubGraphqlResponses = {}

const loadSeeders = () => {
    try {
        // Load the seeders from the configuration file
        for (const key of Object.keys(seeders)) {
            const stub = seeders[key]
            if (stub && stub.response) {
                const response = require('./' + resolveResponsePath(stub.response));
                stubResponses[key] = compileTemplate(response);
            }
        }
        console.log('seeding')
        // Load the graphqlSeeders from the configuration file
        for (const key of Object.keys(graphqlSeeders)) {
            const stub = graphqlSeeders[key]
            if (stub && stub.response) {
                const response = require('./' + resolveResponsePath(stub.response));
                stubGraphqlResponses[key] = response;
            }
        }
    } catch (e) {
        console.error('Error loading seeders:', e);
    }
}

loadSeeders();

/**
 * Resolves Mirage intercept origins from config plus runtime values for NCL appliance deploys.
 * Prod NCL builds use REACT_APP_OCCM_ENDPOINT="/" while platformBackend is the absolute appliance URL.
 */
const resolveMirageOrigins = () => {
    const resolved = origins.filter(Boolean);

    if (isOfflineNCLMode() && typeof window !== "undefined") {
        const { origin } = window.location;
        resolved.push(origin, `${origin}/`);
        return resolved.filter((url) => url !== "/");
    }

    return resolved;
};

/**
 * Returns true when Mirage should handle the request instead of passthrough to the real server.
 */
const shouldMirageHandleRequest = (req) => {
    const mirageOrigins = resolveMirageOrigins();

    if (mirageOrigins.some((url) => req.url.startsWith(url))) {
        return true;
    }

    // NCL prod: occmBackend is "/" so absolute same-origin platformBackend URLs must still intercept
    if (isOfflineNCLMode() && typeof window !== "undefined") {
        try {
            return new URL(req.url, window.location.origin).origin === window.location.origin;
        } catch (_e) {
            return req.url.startsWith("/");
        }
    }

    return false;
};

/** 
 * Seeds the Mirage server with predefined routes and responses.
 * This function sets up the routes based on the seeders defined in the configuration.
 * It also configures passthrough URLs to allow certain requests to bypass Mirage.
 * @param {Object} route - The Mirage route object to configure.
 * @returns {void}
 */
const seed = async (route) => {
    // Set the response timing for the routes based on the configuration
    route.timing = timing;
    route.logging = true;
    // Seed the Mirage server with stub data
    route.urlPrefix = process.env.REACT_APP_OCCM_ENDPOINT

    /**
     * Conditional passthrough for requests.
     * This allows requests to bypass Mirage if they are not meant to be handled by it.
     * It checks if the request has a specific header (`x-offline-mode`) set to true.
     * If the header is not present or set to false, the request will be processed by Mirage.
     * This is useful for allowing certain requests to go through to the actual server
     * while still using Mirage for other requests.
     * @param {Object} req - The request object.
     * @returns {boolean} - Returns true if the request should be passed through, false otherwise.
     */
    route.passthrough("*", (req) => {
        // Defaults to passthrough
        let passthrough = true;

        // If the request is for an arraybuffer, clear the responseType
        if (req.responseType === 'arraybuffer') {
            req.responseType = undefined;
        }
        if (shouldMirageHandleRequest(req)) {
            passthrough = false;
        }

        return passthrough;
    });

    const addDiscoveredWorkingEnvironmentToGraphql = (publicId, clusterName) => {
        const graphqlData = stubGraphqlResponses["working-environments"];
        if (graphqlData?.data?.workingEnvironments?.workingEnvironments) {
            graphqlData.data.workingEnvironments.workingEnvironments.push({
                id: publicId,
                name: clusterName,
                type: "onprem",
                cloudProvider: "onprem",
                agentId: null,
                credentialId: null,
                region: null
            });
        }
    };

    const removeWorkingEnvironmentFromGraphql = (publicId) => {
        const graphqlData = stubGraphqlResponses["working-environments"];
        const workingEnvironments = graphqlData?.data?.workingEnvironments?.workingEnvironments;
        if (workingEnvironments) {
            graphqlData.data.workingEnvironments.workingEnvironments = workingEnvironments.filter(
                (we) => we.id !== publicId
            );
        }
    };

    /** 
     * Iterate over the seeders and set up the routes in Mirage.
     * Each seeder defines a type (GET, POST, etc.) and a URL,
     * along with the response that should be returned when that URL is hit.
     */
    for (const key of Object.keys(seeders)) {
        const stub = seeders[key]
        if (stub && stub.disabled !== true) {

            route[stub.type.toLowerCase()](stub.url, (schema, req) => {
                if (stub.filters) {
                    const stubWithQuery = handleRESTQueryFilter(stub, req);
                    if (stubWithQuery) {
                        return compileTemplate(stubWithQuery);
                    }
                }

                if (stub.payload) {
                    const stubWithPayload = handleRESTQueryPayload(stub, req);
                    if (stubWithPayload) {
                        return compileTemplate(stubWithPayload);
                    }
                }

                if (key === 'delete-from-fleet' || key === 'delete-from-org') {
                    const workingEnvironmentId = req.params.resourceid || req.params.weid;
                    removeWorkingEnvironmentFromGraphql(workingEnvironmentId);
                    return new Response(204);
                }

                if (stub.contentType) {
                    return new Response(
                        stub.status || 200,
                        { 'Content-Type': stub.contentType },
                        stubResponses[key],
                    );
                }

                return stubResponses[key]
            }, { timing: stub.timing || timing });
        }
    }

    // Custom handler: On-Prem discover POST adds new WE to GraphQL working-environments for canvas display
    route.post('/occm/api/onprem/working-environments', (_schema, req) => {
        const body = JSON.parse(req.requestBody);
        const clusterName = body.name || 'Discovered-ONTAP-Cluster';
        const publicId = 'OnPremWorkingEnvironment-Disc' + Date.now();

        addDiscoveredWorkingEnvironmentToGraphql(publicId, clusterName);

        return {
            publicId: publicId,
            name: clusterName,
            tenantId: "Tenant-2345",
            status: { status: "ON" },
            providerName: "ON_PREM"
        };
    });

    // NCL fleet-mode discover POST — storage-management fleet workingenvironments endpoint
    if (isOfflineNCLMode()) {
        route.post('/storage-management/v1/organizations/:orgId/fleets/:fleetId/workingenvironments', (_schema, req) => {
            const body = JSON.parse(req.requestBody);
            const clusterName = body.name || body.clusterName || 'Discovered-ONTAP-Cluster';
            const workingEnvironmentId = 'OnPremWorkingEnvironment-Disc' + Date.now();

            addDiscoveredWorkingEnvironmentToGraphql(workingEnvironmentId, clusterName);

            return {
                aff: false,
                name: clusterName,
                type: 'ON_PREM',
                workingEnvironmentId,
                publicId: workingEnvironmentId
            };
        });
    }

    const ec2Handler = (_schema, req) => {
        let xmlResponse = ''
        if (req.requestBody.startsWith('Action=DescribeVpcs')) {
            xmlResponse = require('./' + resolveResponsePath('data/platform/ec2-vpcs.js')).default;
        }
        if (req.requestBody.startsWith('Action=DescribeSubnets')) {
            xmlResponse = require('./' + resolveResponsePath('data/platform/ec2-subnets.js')).default;
        }
        if (req.requestBody.startsWith('Action=DescribeKeyPairs')) {
            xmlResponse = require('./' + resolveResponsePath('data/platform/ec2-keypairs.js')).default;
        }
        if (req.requestBody.startsWith('Action=DescribeInstanceStatus')) {
            xmlResponse = require('./' + resolveResponsePath('data/platform/ec2-status.js')).default;
        }
        return new Response(200, { "Vary": "accept-encoding", "Content-Type": "text/xml; charset=utf-8", "Transfer-Encoding": "chunked" }, xmlResponse);
    };

    ["https://ec2.us-east-1.amazonaws.com/", "https://ec2.us-east-2.amazonaws.com/", "https://ec2.us-west-1.amazonaws.com/"].forEach((url) => {
        route.post(url, ec2Handler);
    });

    const cloudFormationHandler = (_schema, req) => {
        let xmlResponse = ''
        if (req.requestBody.startsWith('Action=CreateStack')) {
            xmlResponse = require('./' + resolveResponsePath('data/platform/cf-stack.js')).default;
        }
        if (req.requestBody.startsWith('Action=DescribeStacks')) {
            xmlResponse = require('./' + resolveResponsePath('data/platform/cf-status.js')).default;
        }
        if (req.requestBody.startsWith('Action=DescribeStackEvents')) {
            xmlResponse = require('./' + resolveResponsePath('data/platform/cf-events.js')).default;
        }
        return new Response(200, { "Content-Type": "text/xml" }, xmlResponse);
    };

    ["https://cloudformation.us-east-1.amazonaws.com/", "https://cloudformation.us-east-2.amazonaws.com/", "https://cloudformation.us-west-1.amazonaws.com/"].forEach((url) => {
        route.post(url, cloudFormationHandler);
    });

    route.post("https://iam.amazonaws.com/", (_schema, req) => {
        const xmlResponse = require('./' + resolveResponsePath('data/platform/aws-iam.js')).default;
        const res = new Response(200, { "Content-Type": "text/xml" }, xmlResponse);
        return res
    });


    route.post('/v1/services/ui-controller/graphql', (_schema, request) => {
        const requestBody = JSON.parse(request?.requestBody);
        const parsedQuery = parse(requestBody?.query);
        let operationName = parsedQuery?.definitions[0]?.name.value;
        // convert your query name or filter name or json file name to kebabCase wherever required
        operationName = _.kebabCase(operationName);

        // add query name here if for different filter names if you need different set of data.
        // if this is the case then append filter name to query name inside handleGraphqlQueryFilter by adding an entry
        if (operationName === "user-preferences" || operationName === "card-templates-by-resource-type" || operationName === "dashboard") {
            const response = handleGraphqlQueryFilter(parsedQuery, operationName, stubGraphqlResponses, requestBody);
            return response;
        }

        return stubGraphqlResponses[operationName];
    });

    /**
     * Set up passthrough URLs in Mirage.
     * This allows certain requests to bypass Mirage and go directly to the server.
     */
    route.passthrough(...passthroughURLs);
}

export { seed }